import type { ContractParty, ContractRolePresetView } from "@closer/shared";
import {
  CONTRACT_PARTY_POSITION_NAMES,
  effectivePartyPosition,
  ORG_INVITATION_PRESET_TYPE,
} from "@closer/shared";
import { Button, FormField, Modal, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * TARAFI ORTAK ÇALIŞMAYA DAVET (CLOS-1094 · K1 · ADR-0067).
 *
 * ### ⚠ Hedef organizasyon SORULMUYOR
 *
 * Davet tarafın İLETİŞİM E-POSTASINA gidiyor; hangi organizasyon adına kabul edileceğine
 * ALICI kendi oturumunda karar veriyor. Gönderim anında çözseydik — eski davranış —
 * adresin Closer'da kayıtlı olup olmadığı gönderene sızardı (404) ve çok-organizasyonlu
 * adres akışı 409 ile dururdu. Bu pencere kullanıcıdan hiçbir kimlik istemiyor; adres
 * zaten taraf kaydında.
 *
 * ### ⚠ Davet ERİŞİM DEĞİL
 *
 * Kabul edilene kadar sözleşme erişimi oluşmuyor; kabul de kimseyi hukuki taraf yapmıyor.
 * Cümle pencerede duruyor: kullanıcı "davet ettim, karşı taraf görüyor mu?" sorusunu
 * deneyerek değil okuyarak cevaplayabilmeli.
 *
 * ### ⚠ Sunucu reddi PENCEREDE
 *
 * Mükerrer davet, kendi tarafımızı davet etme gibi redler (409) toast'a değil formun
 * içine yazılıyor ve pencere kapanmıyor: kullanıcı sebebi okuyup düzeltebilmeli. Metin
 * SUNUCUDAN, ekran kendi cümlesini uydurmuyor (kriter 6).
 */
export function PartyInviteDialog({
  open,
  onClose,
  contractId,
  taraf,
}: {
  open: boolean;
  onClose: () => void;
  contractId: string;
  /** Davet edilecek taraf — pencere kapalıyken `null`. */
  taraf: ContractParty | null;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  /** Davette kullanılacak rol preseti; boş = açık set (yalnız görüntüleme). */
  const [preset, setPreset] = useState("");
  const [hata, setHata] = useState<string | null>(null);

  /** Pencere AÇILDIĞINDA sıfırlanıyor; kapalıyken eski seçim ve eski hata kalmasın. */
  useEffect(() => {
    if (open) {
      setPreset("");
      setHata(null);
    }
  }, [open]);

  /**
   * DAVET ROL PRESETLERİ (CLOS-149 `[130]`).
   *
   * ⚠ ANAHTAR PRESET YÖNETİM SAYFASIYLA AYNI (`tenantKey(tenantId, "contract-role-presets")`):
   * iki yüzey aynı listeyi okuyor ve önbelleği paylaşıyor. Ayrı anahtar verseydik preset
   * eklendikten sonra davet penceresi bayat listeyi göstermeye devam ederdi.
   */
  const presetler = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract-role-presets"),
    queryFn: async () =>
      (await api<ContractRolePresetView[]>("/api/contract-role-presets", { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const gonder = useMutation({
    mutationFn: async (hedef: ContractParty) =>
      api(`/api/contracts/${contractId}/org-invitations`, {
        method: "POST",
        tenantId,
        /** ⚠ Uç `idempotency()` zinciriyle korunuyor: çift tıklama iki davet yazmasın. */
        headers: { "idempotency-key": idempotency.uret() },
        /**
         * ⚠ ŞEMA TAM BİRİNİ İSTİYOR: `presetId` ile `permissions` birlikte gönderilemez
         * (`createContractOrgInvitationSchema` refine'ı). Preset seçilmediğinde gövde
         * açık setiyle gidiyor — eski formun varsayılan davranışı aynen korunuyor.
         */
        body: JSON.stringify({
          partyId: hedef.id,
          targetEmail: hedef.email,
          ...(preset === "" ? { permissions: ["contract.view"] } : { presetId: preset }),
        }),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      /**
       * ⚠ ÜÇ SORGU BİRDEN: taraf satırının rozeti ("Davet bekliyor"), paylaşım bölümündeki
       * davet listesi ve paylaşım bağlamı aynı gerçeği üç açıdan gösteriyor. Yalnız birini
       * tazeleseydik ekran kendi kendisiyle çelişirdi.
       */
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "taraflar"),
        }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "org-davetleri"),
        }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-baglami"),
        }),
      ]);
      toast.success("Davet gönderildi");
      onClose();
    },
    onError: (err: unknown) =>
      setHata(err instanceof ApiRequestError ? err.message : "Davet gönderilemedi"),
  });

  if (!taraf) return null;

  const konum = effectivePartyPosition(taraf);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ortak çalışmaya davet et"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          {/* ⚠ Adres yoksa gönderilecek bir şey yok; düğme kapalı, bölüm sebebini yazıyor. */}
          <Button
            onClick={() => gonder.mutate(taraf)}
            loading={gonder.isPending}
            disabled={!taraf.email}
          >
            Davet gönder
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <dl data-testid="davet-ozeti" className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-fg-muted">Taraf</dt>
          <dd className="font-medium text-fg-strong">{taraf.displayName}</dd>
          <dt className="text-fg-muted">Konum</dt>
          {/* ⚠ Konum bilinmiyorsa UYDURULMUYOR (CLOS-1108): "Belirtilmedi" yazıyor. */}
          <dd className="text-fg">
            {konum ? CONTRACT_PARTY_POSITION_NAMES[konum] : "Belirtilmedi"}
          </dd>
          <dt className="text-fg-muted">Alıcı e-posta</dt>
          <dd className="text-fg">{taraf.email ?? "—"}</dd>
        </dl>

        {/*
          ⚠ YALNIZ ORGANİZASYON PRESETLERİ: davet haricî ORGANİZASYON daveti. `internal`
          ya da `external_person` preseti sunucuda zaten reddediliyor; listelemek,
          kullanıcıya reddedilecek bir seçenek sunmak olurdu.
        */}
        <FormField
          label="Davet rolü"
          hint="Preset seçilmezse davet yalnız görüntüleme yetkisiyle gider."
        >
          <Select
            id="taraf-davet-preset"
            value={preset}
            onChange={(olay) => setPreset(olay.target.value)}
          >
            <option value="">Varsayılan (yalnız görüntüleme)</option>
            {(presetler.data ?? [])
              .filter((p) => p.participantType === ORG_INVITATION_PRESET_TYPE)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </Select>
        </FormField>

        <p className="text-sm text-fg-muted">
          Davet kabul edilene kadar sözleşme erişimi oluşmaz; alıcı daveti hangi organizasyonu adına
          kabul edeceğini kendi seçer.
        </p>

        {hata ? (
          <p role="alert" className="text-sm text-danger">
            {hata}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
